import { useNavigate } from "react-router-dom";
import GridView from "../../components/controls/GridView/GridView";
import userService, { User } from "../../services/userService";
import "./user-listing.module.css";
import { MenuItem } from "../../models/menu";
import { UserTransactionLimit } from "../../models/interfaces";
import FormModal from "../../components/controls/FormModal/FormModal";
import { ISearchablePage } from "../../models/props";
import { useEffect, useState } from "react";
import TransactionLimit from "../../components/controls/TransactionLimit/TransactionLimit";
import roleService, { Role } from "../../services/roleService";
import FormCommand from "../../components/controls/FormCommand/FormCommand";
import Frame from "../../components/controls/Frame/Frame";
import ComboBox from "../../components/controls/ComboBox";
import TextBox from "../../components/controls/TextBox/TextBox";
import CheckBox from "../../components/controls/Checkbox/CheckBox";
import { MdWarning } from "react-icons/md";

export interface UserListingPageProps extends ISearchablePage<User> {}

export const UserListingPage = (props: UserListingPageProps) => {
  const [showContextContent, setShowContextContent] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState<User>();

  const navigate = useNavigate();

  const renderRoles = async () => {
    const res = await roleService.getRoles({});
    if (res.status) {
      setRoles(res.data ?? []);
    }
  };

  useEffect(() => {
    renderRoles();
  }, []);

  const toggleShowRoles = (): void => setShowRoles(!showRoles);

  function getContextMenu(): MenuItem<User>[] {
    return [
      {
        name: "Edit User Profile",
        onClick: (e) => {
          navigate(`${e.id}`, {
            relative: "route",
          });
        },
      },
      {
        name: "Delete User Profile",
        onClick: (e, callBackFn) => {
          const ans = window.confirm(
            "Are you sure you want to delete user \nPlease note that this operation cannot be reversed" +
              e.userFullName
          );
          if (ans !== true) return;

          const deleteUserProfile = async () => {
            const resp = await userService.deleteUserProfile(Number(e.id));
            if (resp.status) {
              alert(resp.message);
              callBackFn?.();
            } else {
              alert(resp.message);
            }
          };

          deleteUserProfile();
        },
      },
      {
        name: "Configure Transaction Limit",
        onClick: (e) => {
          setShowContextContent(!showContextContent);
          setUser(e);
        },
      },
      {
        name: "Assign roles",
        onClick: (e) => {
          setUser(e);
          setShowRoles(!showRoles);

          const getUserRolesFn = async () => {
            const resp = await userService.getRoles(Number(e.id));
            if (resp.status) {
              setSelectedRoles(resp.data?.map((d) => d.id ?? 0) ?? []);
            }
          };

          getUserRolesFn();
        },
      },
    ];
  }

  const getUsers = async () => {
    return userService.getUsers({
      pageIndex: 1,
    });
  };

  useEffect(() => {
    const fn = async () => {
      const res = await getUsers();
      if (res.status) {
        setUsers(res.data ?? []);
      }
    };

    fn();
  }, []);

  return (
    <div className="row">
      <div className="col">
        <Frame>
          <h4>Users</h4>
          <GridView<User>
            showSearchBar={true}
            context="Users"
            showAddButton={true}
          >
            <GridView.Header>
              <tr>
                <th>Name</th>
                <th>LoginId</th>
                <th>Email</th>
                <th>Phone</th>
              </tr>
            </GridView.Header>
            <GridView.Content>
              {users.map((d) => (
                <tr>
                  <td>{d.userFullName}</td>
                  <td>{d.userName}</td>
                  <td>{d.email}</td>
                  <td>{d.phoneNo}</td>
                </tr>
              ))}
            </GridView.Content>
          </GridView>

          {showContextContent /*&& user !== null*/ && (
            <FormModal onClose={() => setShowContextContent(false)}>
              <TransactionLimit<UserTransactionLimit>
                displayTitle={() => {
                  return <h5>{user?.userFullName}</h5>;
                }}
                onGetTransactionLimits={async () => {
                  return await userService.getTransactionLimits(user?.id ?? 0);
                }}
                onSaveChanges={async (limit) => {
                  const resp = await userService.setUserTransactionLimit(
                    limit,
                    user?.id ?? 0
                  );
                  if (resp.status) {
                    return resp.data;
                  }
                }}
                onDeleteLimit={async (limit, limits) => {
                  const ans = window.confirm(
                    "Are you sure you want to delete this limit"
                  );
                  if (ans !== true) return;

                  const resp = await userService.deleteUserTransactionLimit(
                    limit,
                    user?.id ?? 0
                  );
                  if (resp.status && resp.data) {
                    const newLimits = limits.filter(
                      (d) =>
                        !(
                          d.userId === limit.userId &&
                          d.period === limit.period &&
                          d.transactionType === limit.transactionType
                        )
                    );
                    return newLimits;
                  }
                }}
              />
            </FormModal>
          )}

          {showRoles && (
            <FormModal onClose={() => setShowRoles(false)}>
              <div>
                <div
                  className="list-group"
                  style={{ maxHeight: "20em", overflowY: "scroll" }}
                >
                  {roles?.map((d) => {
                    const isChecked = selectedRoles.includes(Number(d.id));
                    return (
                      <div className="list-group-item">
                        <CheckBox
                          text={d.name ?? ""}
                          id={`chkrole_${d.id}`}
                          onChecked={(value) => {
                            if (value) {
                              setSelectedRoles([
                                ...selectedRoles,
                                Number(d.id),
                              ]);
                            } else {
                              setSelectedRoles(
                                selectedRoles.filter(
                                  (roleId) => roleId !== d.id
                                )
                              );
                            }
                          }}
                          checked={isChecked}
                        />
                      </div>
                    );
                  })}
                </div>

                <FormCommand
                  onCancel={toggleShowRoles}
                  onSubmit={() => {
                    const assignRolesFn = async () => {
                      const res = await roleService.assignRoles(
                        user?.id,
                        selectedRoles
                      );
                      if (res.status) {
                        alert("Role(s) applied successfully");
                        toggleShowRoles();
                      }
                    };

                    assignRolesFn();
                  }}
                />
              </div>
            </FormModal>
          )}

          {user && (
            <FormModal onClose={() => setUser(undefined)}>
              <UserRegistrationForm currentUser={user} />
            </FormModal>
          )}
        </Frame>
      </div>
    </div>
  );
};

export type UserRegistrationFormProps = {
  currentUser: User;
};

export function UserRegistrationForm(props: UserRegistrationFormProps) {
  const [user, setUser] = useState<User>(props.currentUser);

  return (
    <div className="row">
      <div className="col">
        <TextBox
          id="userName"
          text="User Name"
          placeholder="Enter user name"
          onChange={(e) => {
            setUser({ ...user, userName: e.currentTarget.value });
          }}
        />

        {user?.id && (
          <div className="row">
            <div className="col">
              <TextBox
                id="password"
                text="Password"
                placeholder="Enter your password"
                type="password"
                onChange={(e) => {
                  setUser({ ...user, password: e.currentTarget.value });
                }}
              />
            </div>
            <div className="col">
              <TextBox
                id="confirmPassword"
                text="Confirm Password"
                placeholder="Confirm your password"
                type="password"
                onChange={(e) => {
                  setUser({ ...user, password: e.currentTarget.value });
                }}
              />
            </div>
          </div>
        )}

        <div className="row mb-2">
          <div className="col">
            <TextBox
              id="phoneNo"
              text="Phone No"
              type="tel"
              onChange={(e) => {
                setUser({ ...user, phoneNo: e.currentTarget.value });
              }}
            />

            <span className="badge bg-warning">
              <MdWarning />
              Not verified
            </span>
          </div>
          <div className="col">
            <TextBox
              id="email"
              text="Email"
              type="email"
              onChange={(e) => {
                setUser({ ...user, email: e.currentTarget.value });
              }}
            />
            <span className="badge bg-warning">
              <MdWarning />
              Not verified
            </span>
          </div>
        </div>

        <hr />

        <CheckBox
          id="isApprovingOfficer"
          text="Approving Officer"
          onChecked={(value) => {
            setUser({ ...user, approvingOfficer: value });
          }}
          type="switchbox"
        />

        <CheckBox
          id="mustChangePassword"
          text="Must Change Password"
          onChecked={(value) => {
            setUser({ ...user, mustChangePassword: value });
          }}
          type="switchbox"
        />

        <CheckBox
          id="mustSetupSecurityQuestion"
          text="Enforce Security Question Setup"
          onChecked={(value) => {
            setUser({ ...user, mustSetupSecurityQuestion: value });
          }}
          type="switchbox"
        />

        <div className="row">
          <div className="col">
            <TextBox
              id="transactionLimit"
              text="Transaction Limit"
              placeholder="Specify Transaction Limit"
              onChange={(e) => {
                setUser({
                  ...user,
                  transactionLimit: Number(e.currentTarget.value),
                });
              }}
              type="number"
              min={0}
              step={1000}
            />
          </div>

          <div className="col">
            <ComboBox
              id="transactionLimitType"
              text="Transaction Limit Type"
              prompt="Select Transaction Limit Type"
              onChange={(e) => {
                setUser({
                  ...user,
                  transactionLimitType: Number(e.currentTarget.value),
                });
              }}
            />
          </div>
        </div>

        <ComboBox
          id="location"
          text="Select Location"
          prompt="Select Location(Branch)"
          onChange={(e) => {
            setUser({ ...user, branchId: Number(e.currentTarget.value) });
          }}
        />

        <FormCommand
          onSubmit={async () => {
            if (user?.id) {
              const res = await userService.updateUserProfile(
                user?.id ?? 0,
                user
              );
              console.log(res.message);
            } else {
              const res = await userService.registerUser(user);
              console.log(res.message);
              if (res.status) {
                //TODO: use a context here to display the alert of use redux to display the alert
              }
            }
          }}
        />
      </div>
    </div>
  );
}
